import {
	FC,
	ReactNode,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import { yupResolver } from '@hookform/resolvers/yup';
import { CircularProgress } from '@mui/material';
import dayjs from 'dayjs';

import {
	personalDetailsManager,
	useDTicketSubscriptionOffering,
	useNotificationPermission,
	usePassengerPushes,
	usePaymentMethod,
	useSubscription,
	useSubscriptionActions,
	useSubscriptionTicketActions,
	useUser,
	useUserActions,
} from '@mopla/business-logic';
import { useLogger } from '@mopla/logger';
import {
	PaymentPortal,
	usePaymentPortalControls,
	useRestoreState,
} from '@mopla/payment-flow';
import {
	AdaptiveActionsBlock,
	CheckmarkIcon,
	Loader,
	MplButton,
	MplErrorModal,
	PermissionsModal,
	RecaptchaHandle,
	StayInformedAnim,
	usePassengerRoutingHelpers,
} from '@mopla/ui';
import { getDTicketType, isAfterTenthOfMonth } from '@mopla/utils';

import passApi from '../../../api/passApi';
import {
	pushPaymentMethodAddedEvent,
	pushPurchaseEvent,
	pushSelectSubscriptionMonthEvent,
} from '../../../app/userTracking/events';

import { ConfirmationStep } from './steps/Confirmation';
import { FirstStep } from './steps/First';
import { AnotherRecipientBlock, SecondStep } from './steps/Second';
import { SummaryStep } from './steps/Summary';
import {
	CONFIRMATION_STEP,
	defaultValues,
	invoiceRecipientDefaultValues,
	MONTH_SELECTION_STEP,
	PAYMENT_METHOD_STEP,
	PERSONAL_DATA_STEP,
	SUMMARY_STEP,
} from './constants';
import { DTicketPaymentMethod } from './DTicketPaymentMethod';
import {
	ContentWrapper,
	CustomStepper,
	DrawerContentWrapper,
	LoaderWrapper,
	ScrollableWrapper,
	ThirdStepWrapper,
} from './DTicketSubscription.styles';
import { DTicketSubscriptionRecaptcha } from './DTicketSubscriptionRecaptcha';
import { DTicketSubscriptionWrapper } from './DTicketSubscriptionWrapper';
import { getSavedAlternateInvoiceAddress } from './helpers';
import { PriceInfoBlock } from './PriceInfoBlock';
import {
	InvoiceRecipientFormValidationSchema,
	SubscriptionFormValidationSchema,
} from './SubscriptionValidation';
import {
	IDTicketRestoreState,
	IInvoiceRecipientFormValues,
	ISubscriptionFormValues,
} from './types';
import { ValidityModal } from './ValidityModal';

interface IDTicketSubscriptionProps {
	open: boolean;
	onClose: VoidFunction;
	loading?: boolean;
}

export const DTicketSubscription: FC<IDTicketSubscriptionProps> = (props) => {
	const logger = useLogger('payment');
	const { open, onClose, loading = false } = props;
	const { t } = useTranslation(['dticket']);
	const [query] = useSearchParams();
	const [activeStep, setActiveStep] = useState(MONTH_SELECTION_STEP);
	const [error, setError] = useState(false);
	const navigate = useNavigate();
	const { subscriptionData: subscription } = useSubscription();
	const { fetchSubscriptions } = useSubscriptionActions();
	const { fetchTickets } = useSubscriptionTicketActions();
	const [isSubscribing, setIsSubscribing] = useState(false);
	const recaptchaRef = useRef<RecaptchaHandle>(null);
	const { navigateToDTicketAlreadyExistScreen } = usePassengerRoutingHelpers();
	const { userData } = useUser();
	const { sendMessagingToken } = usePassengerPushes();
	const {
		showPermissionModal,
		checkAndRequestPermission,
		closePermissionModal,
	} = useNotificationPermission();
	const { openPaymentPortal, isPaymentPortalOpened } =
		usePaymentPortalControls();

	const methods = useForm<ISubscriptionFormValues>({
		defaultValues: {
			...defaultValues,
			firstName: userData?.firstName,
			surname: userData?.lastName,
			phoneNumber: userData?.phone,
			street: userData?.address?.street,
			streetNumber: userData?.address?.streetNumber,
			postCode: userData?.address?.zipCode,
		},
		resolver: yupResolver(SubscriptionFormValidationSchema),
		mode: 'onBlur',
	});
	const { formState, watch, trigger, reset, getValues } = methods;
	const { isDirty, errors } = formState;
	const monthOfStartValue = watch('monthOfStart');

	const { subscriptionOffering, subscriptionOfferByMonth } =
		useDTicketSubscriptionOffering(monthOfStartValue);
	const isJobticket = !!subscriptionOffering?.isJobticket;
	const dTicketType = getDTicketType(subscriptionOffering?.subscriptionType);
	const isPriceZero = subscriptionOffering?.price === 0;
	const { fetchUser } = useUserActions();
	useEffect(() => {
		if (
			open &&
			subscription?.id &&
			!isSubscribing &&
			activeStep === MONTH_SELECTION_STEP
		) {
			navigateToDTicketAlreadyExistScreen();
		}
	}, [
		subscription?.id,
		open,
		isSubscribing,
		activeStep,
		navigateToDTicketAlreadyExistScreen,
	]);

	const isMonthSelectionStep = activeStep === MONTH_SELECTION_STEP;
	const isPersonalDataStep = activeStep === PERSONAL_DATA_STEP;
	const isPaymentStep = activeStep === PAYMENT_METHOD_STEP;
	const isSummaryStep = activeStep === SUMMARY_STEP;
	const isConfirmationStep = activeStep === CONFIRMATION_STEP;

	const [showInvoiceRecipientForm, setShowInvoiceRecipientForm] =
		useState(false);
	const [isAcceptTariffs, setIsAcceptTariffs] = useState(false);
	const [isAcceptEarlyServiceStart, setIsAcceptEarlyServiceStart] =
		useState(false);

	const [isValidityModalVisible, setIsValidityModalVisible] = useState(false);
	const {
		defaultPaymentMethod,
		isPaymentMethodLoading,
		isPaymentMethodSaving,
	} = usePaymentMethod();

	const isPaymentLoading = isPaymentMethodLoading || isPaymentMethodSaving;

	const steps = useMemo(() => {
		if (!userData) {
			return [
				MONTH_SELECTION_STEP,
				PERSONAL_DATA_STEP,
				PAYMENT_METHOD_STEP,
				SUMMARY_STEP,
			];
		}
		const flowSteps = [MONTH_SELECTION_STEP];
		const needPersonalData = userData?.pleaseComplete;
		const hasDefaultPaymentMethod = userData?.hasDefaultStripePaymentMethod;

		if (needPersonalData) flowSteps.push(PERSONAL_DATA_STEP);
		if (!hasDefaultPaymentMethod && !isPriceZero)
			flowSteps.push(PAYMENT_METHOD_STEP);
		flowSteps.push(SUMMARY_STEP);

		return flowSteps;
	}, [userData, isPriceZero]);

	const stepsAmount = steps.length;
	const activeStepIndex = steps.findIndex((step) => step === activeStep);

	const savedAlternateInvoiceAddress = getSavedAlternateInvoiceAddress(
		userData?.alternateInvoiceAddress || null
	);
	const invoiceFormMethods = useForm<IInvoiceRecipientFormValues>({
		defaultValues: invoiceRecipientDefaultValues,
		resolver: yupResolver(InvoiceRecipientFormValidationSchema),
		mode: 'onBlur',
	});
	const {
		formState: invoiceFormState,
		watch: watchInvoiceFields,
		trigger: triggerInvoiceForm,
		reset: resetInvoiceForm,
	} = invoiceFormMethods;
	const { isDirty: isInvoiceDirty, errors: invoiceErrors } = invoiceFormState;

	useEffect(() => {
		if (!userData || !userData.address) return;
		if (!subscription || !subscription.companyName) return;

		const { lastName, dateOfBirth, address, firstName, phone, metaData } =
			userData;
		const name =
			(isJobticket && (metaData?.jobticketFirstName as unknown as string)) ||
			firstName;
		const surname =
			(isJobticket && (metaData?.jobticketLastName as unknown as string)) ||
			lastName;
		const personalData: ISubscriptionFormValues = {
			monthOfStart: getValues('monthOfStart') || '',
			firstName: name || '',
			surname: surname || '',
			phoneNumber: phone || '',
			day: dayjs(dateOfBirth).date().toString(),
			month: dayjs(dateOfBirth).month().toString(),
			year: dayjs(dateOfBirth).year().toString(),
			street: address.street || '',
			streetNumber: address.streetNumber || '',
			postCode: address.zipCode || '',
			location: address.city || '',
			companyName: subscription.companyName || '',
		};
		reset(personalData, { keepDirty: true });
	}, [userData, isJobticket, reset, getValues, subscription]);

	/** State to persist for restoration after payment redirect */
	const stateToRestore = useMemo<IDTicketRestoreState>(
		() => ({
			activeStep,
			monthOfStart: monthOfStartValue,
		}),
		[activeStep, monthOfStartValue]
	);

	/** Handle state restoration after payment redirect */
	const handleStateRestored = useCallback(
		(restoredState: IDTicketRestoreState) => {
			console.log('[DTicketSubscription] Restoring state:', restoredState);

			try {
				/** Restore active step */
				setActiveStep(restoredState.activeStep);

				/** Restore month selection */
				if (restoredState.monthOfStart) {
					methods.setValue('monthOfStart', restoredState.monthOfStart, {
						shouldDirty: true,
						shouldValidate: true,
					});
				}
			} catch (error) {
				console.error('[DTicketSubscription] State restoration failed:', error);
				/** Fallback to first step on error */
				setActiveStep(MONTH_SELECTION_STEP);
			}
		},
		[methods]
	);

	useRestoreState({
		restoreState: stateToRestore,
		onStateRestored: handleStateRestored,
	});

	/** Query params for restoring after payment redirect with isBankRedirect flag */
	const queryToRestore = useMemo(() => {
		const newQuery = new URLSearchParams(query);
		newQuery.set('isBankRedirect', '1');

		return newQuery.toString();
	}, [query]);

	const nextStep = () => {
		const nextIndex = activeStepIndex + 1;
		setActiveStep(
			nextIndex > stepsAmount ? steps[stepsAmount - 1] : steps[nextIndex]
		);
	};
	const toggleInvoiceForm = () => setShowInvoiceRecipientForm((prev) => !prev);

	const nextStepHandler = async () => {
		const personalData = watch();

		if (isMonthSelectionStep) {
			const isValid = await trigger('monthOfStart');
			setIsValidityModalVisible(isAfterTenthOfMonth(personalData.monthOfStart));

			if (!isValid) return;

			pushSelectSubscriptionMonthEvent(personalData.monthOfStart);

			return nextStep();
		}

		if (isPersonalDataStep) {
			const isValid = await trigger();
			if (!isValid) return;
			if (showInvoiceRecipientForm) {
				const isInvoiceDataValid = await triggerInvoiceForm();
				if (!isInvoiceDataValid) return;
			}
			try {
				const personalDetails = {
					firstName: personalData.firstName.trim(),
					lastName: personalData.surname.trim(),
					phone: personalData.phoneNumber.trim(),
					dateOfBirth:
						personalData.year && personalData.month && personalData.day
							? new Date(
									`${personalData.year}-${personalData.month}-${personalData.day}`
							  ).toISOString()
							: null,
					street: personalData.street.trim(),
					streetNumber: personalData.streetNumber.trim(),
					zipcode: personalData.postCode.trim(),
					city: personalData.location.trim(),
				};
				await passApi.post('/api/command/addPassengerDetails', personalDetails);
				personalDetailsManager.set('completed');
				try {
					const invoiceFormValues = watchInvoiceFields();
					const invoiceDetails = {
						firstName: invoiceFormValues.firstName.trim(),
						lastName: invoiceFormValues.surname.trim(),
						company: invoiceFormValues.company.trim(),
						street: invoiceFormValues.street.trim(),
						streetNumber: invoiceFormValues.streetNumber.trim(),
						zipcode: invoiceFormValues.postCode.trim(),
						city: invoiceFormValues.location.trim(),
					};
					await passApi.post(
						'/api/command/setAlternateInvoiceAddress',
						invoiceDetails
					);
				} catch (e) {
					setError(true);
					console.error('Error on saving invoice details', e);
				}
				return nextStep();
			} catch (e) {
				setError(true);
				console.error('Error on saving passenger details', e);
			}
		}
		if (isPaymentStep) {
			logger.info('DTicketSubscription: PaymentPortal open initiated');
			return openPaymentPortal();
		}

		try {
			if (isSubscribing) {
				return;
			}

			// Set states IMMEDIATELY to disable button before recaptcha
			setIsSubscribing(true);

			if (!recaptchaRef.current) {
				throw new Error('Recaptcha ref is not available');
			}

			const recaptchaToken = await recaptchaRef.current.executeRecaptcha();

			if (!recaptchaToken) {
				throw new Error('Recaptcha token is missing');
			}

			await passApi.post('/api/passengers/subscription/create', {
				startDate: dayjs(personalData.monthOfStart).format('YYYY-MM-01'),
				recaptchaToken,
			});

			fetchSubscriptions();
			fetchTickets();
			fetchUser();
			pushPurchaseEvent({
				value: subscriptionOffering?.price,
				shipping: 0.0,
				currency: 'EUR',
				items: [
					{
						item_name: 'Deutschlandticket',
						item_id: 'mp1122',
						price: subscriptionOffering?.price,
						item_brand: 'not necessary',
						item_category: 'Deutschlandticket',
						item_variant: 'Deutschlandticket',
						quantity: 1,
					},
				],
			});

			return setActiveStep(CONFIRMATION_STEP);
		} catch (e) {
			setError(true);
			console.log('Error on creating subscription: ', e);
		} finally {
			setIsSubscribing(false);
		}
	};

	const closeError = () => setError(false);

	// Check notification permission after successful Deutschland ticket purchase
	// Pass 'action' context to always ask if not granted (not persisted)
	useEffect(() => {
		if (activeStep === CONFIRMATION_STEP) {
			checkAndRequestPermission('action');
		}
	}, [activeStep, checkAndRequestPermission]);

	const previousStepHandler = () => {
		const prevIndex = activeStepIndex - 1;

		if (prevIndex < 0) return handleClose();

		return setActiveStep(steps[prevIndex]);
	};
	const redirectToHomePage = () =>
		!Capacitor.isNativePlatform()
			? navigate(
					{ pathname: '/welcome', search: 'tryOurApp' },
					{ replace: true }
			  )
			: navigate('/home', { replace: true });

	const handleClose = () => {
		fetchSubscriptions();
		fetchTickets();
		// Condition if subscription flow started by passwordless
		const startedByPasswordless =
			query.has('subscription') && query.has('oobCode');
		startedByPasswordless ? redirectToHomePage() : onClose();

		setIsAcceptTariffs(false);
		setActiveStep(MONTH_SELECTION_STEP);
		reset(defaultValues);
		if (showInvoiceRecipientForm) {
			resetInvoiceForm(invoiceRecipientDefaultValues);
			setShowInvoiceRecipientForm(false);
		}
	};

	const handleSubmitValidityModal = () => {
		setIsValidityModalVisible(false);
	};

	const handleCanceltValidityModal = () => {
		setIsValidityModalVisible(false);
		previousStepHandler();
	};

	const getContent = (): ReactNode => {
		if (isMonthSelectionStep)
			return (
				<FirstStep
					subscriptionOffering={subscriptionOffering}
					selectedMonth={subscriptionOfferByMonth}
				/>
			);
		// TODO: redesign-dticket: deadcode
		if (isPersonalDataStep) return <SecondStep />;
		if (isPaymentStep)
			return (
				<ContentWrapper>
					<ThirdStepWrapper>
						<DTicketPaymentMethod isPreparing={isPaymentLoading} />
					</ThirdStepWrapper>
				</ContentWrapper>
			);

		if (isSubscribing) {
			return (
				<LoaderWrapper>
					<Loader />
				</LoaderWrapper>
			);
		}

		return (
			<SummaryStep
				invoiceData={savedAlternateInvoiceAddress || watchInvoiceFields()}
				showInvoiceRecipientBlock={
					!!savedAlternateInvoiceAddress || showInvoiceRecipientForm
				}
				isAcceptTariffs={isAcceptTariffs}
				setIsAcceptTariffs={setIsAcceptTariffs}
				isAcceptEarlyServiceStart={isAcceptEarlyServiceStart}
				setIsAcceptEarlyServiceStart={setIsAcceptEarlyServiceStart}
				isPriceZero={isPriceZero}
				dTicketType={dTicketType}
			/>
		);
	};
	const getHeader = (): string => {
		if (isMonthSelectionStep) return t('header.book_ticket', { dTicketType });
		if (isPersonalDataStep) return t('header.contact_data');
		if (isPaymentStep) return t('header.payment');
		if (isConfirmationStep) return t('header.thanks_for_subscription');
		return t('header.summary');
	};
	const getButtonText = (): string => {
		if (isMonthSelectionStep || isPersonalDataStep) return t('button.next');
		if (isPaymentStep) return t('button.add_payment_method');

		return t('button.subscribe');
	};

	const getIsDisabled = () => {
		if (isMonthSelectionStep) return !isDirty || !!errors.monthOfStart;

		const invoiceFormHasErrors =
			showInvoiceRecipientForm &&
			(!isInvoiceDirty || Object.keys(invoiceErrors).length > 0);
		const hasErrors = !isDirty || Object.keys(errors).length > 0;

		if (isSummaryStep)
			return !isAcceptTariffs || !isAcceptEarlyServiceStart || isSubscribing;

		return loading || hasErrors || invoiceFormHasErrors;
	};

	const getShowLoader = () => {
		return false;
	};

	const getSubmitTestId = () => {
		if (isMonthSelectionStep || isPersonalDataStep) {
			return 'dticket-subscription-next-button';
		}

		if (isPaymentStep) {
			return 'dticket-subscription-addPaymentMethod-button';
		}

		return 'dticket-subscription-submit-button';
	};

	const stepReactKey = useMemo(
		/* Dynamic React key ensures the component is re-rendered between steps,
		preventing the scroll position from being retained across different steps. */
		() =>
			`${isMonthSelectionStep}-${isPersonalDataStep}-${isPaymentStep}-${isSummaryStep}-${isConfirmationStep}`,
		[
			isConfirmationStep,
			isMonthSelectionStep,
			isPaymentStep,
			isPersonalDataStep,
			isSummaryStep,
		]
	);

	const InvoiceRecipientForm = isPersonalDataStep ? (
		<FormProvider {...invoiceFormMethods}>
			<AnotherRecipientBlock
				showInvoiceRecipientForm={showInvoiceRecipientForm}
				toggleInvoiceForm={toggleInvoiceForm}
			/>
		</FormProvider>
	) : null;

	useEffect(() => {
		/** A new payment method was added -> proceed to the next step */
		if (isPaymentStep && defaultPaymentMethod) {
			setActiveStep(SUMMARY_STEP);

			pushPaymentMethodAddedEvent();
		}
	}, [defaultPaymentMethod, isPaymentStep]);

	return (
		<>
			<MplErrorModal
				open={error}
				testId="DTicketSubscription-MplErrorModal"
				buttons={
					<MplButton fullWidth onClick={closeError}>
						{t('errors:button.close')}
					</MplButton>
				}
			/>
			{isPaymentPortalOpened && (
				<PaymentPortal restorePage="/home" restoreQuery={queryToRestore} />
			)}
			<ValidityModal
				isOpen={isValidityModalVisible}
				onSubmit={handleSubmitValidityModal}
				onCancel={handleCanceltValidityModal}
			/>
			<FormProvider {...methods}>
				<DTicketSubscriptionWrapper
					open={open}
					title={getHeader()}
					onClose={handleClose}
					onBack={activeStepIndex < 0 ? undefined : previousStepHandler}
					isConfirmationStep={isConfirmationStep}
					content={
						<DrawerContentWrapper>
							{isConfirmationStep && (
								<ConfirmationStep
									onSubmit={handleClose}
									distributor={subscription?.companyName || ''}
									dTicketType={dTicketType}
								/>
							)}
							{!isConfirmationStep && (
								<ScrollableWrapper key={stepReactKey}>
									{loading && (
										<LoaderWrapper>
											<CircularProgress />
										</LoaderWrapper>
									)}
									{!loading && getContent()}
									{!loading && InvoiceRecipientForm}
								</ScrollableWrapper>
							)}
						</DrawerContentWrapper>
					}
					footer={
						<>
							{!isConfirmationStep && (
								<>
									<CustomStepper
										variant="dots"
										steps={stepsAmount}
										position="static"
										activeStep={activeStepIndex}
										nextButton={null}
										backButton={null}
									/>
									<AdaptiveActionsBlock
										topBlock={
											isSummaryStep && (
												<PriceInfoBlock
													priceToPay={subscriptionOfferByMonth?.price}
												/>
											)
										}
										mainBlock={
											<MplButton
												color="primaryRevert"
												data-testid={getSubmitTestId()}
												onClick={nextStepHandler}
												disabled={getIsDisabled()}
												showLoader={getShowLoader()}
												fullWidth
											>
												{getButtonText()}
											</MplButton>
										}
										enforceMobile
									/>
								</>
							)}
							{isConfirmationStep && (
								<AdaptiveActionsBlock
									mainBlock={
										<MplButton
											fullWidth
											testId="dTicketSubscription-concludeButton"
											color="primaryRevert"
											onClick={handleClose}
											endIcon={<CheckmarkIcon />}
										>
											{t('booking:button.conclude')}
										</MplButton>
									}
									enforceMobile
								/>
							)}
						</>
					}
				/>
				{!isPaymentStep && (
					<DTicketSubscriptionRecaptcha recaptchaRef={recaptchaRef} />
				)}
			</FormProvider>
			<PermissionsModal
				isOpen={showPermissionModal}
				permissionRequestTitle={t(
					'permissions:notification.passenger.request.title'
				)}
				permissionRequestDescription={t(
					'permissions:notification.passenger.request.description'
				)}
				permissionRequestButtonAcceptText={t(
					'permissions:notification.passenger.request.button_accept'
				)}
				permissionRequestButtonSkipText={t(
					'permissions:notification.passenger.request.button_skip'
				)}
				permissionDeniedTitle={t(
					'permissions:notification.passenger.denied.title'
				)}
				permissionDeniedDescription={t(
					'permissions:notification.passenger.denied.description'
				)}
				permissionDeniedButtonAcceptText={t(
					'permissions:notification.passenger.denied.button_accept'
				)}
				permissionDeniedButtonSkipText={t(
					'permissions:notification.passenger.denied.button_skip'
				)}
				animationElement={<StayInformedAnim />}
				checkPermissionsCallback={FirebaseMessaging.checkPermissions}
				askPermissionsCallback={FirebaseMessaging.requestPermissions}
				onPermissionsGranted={sendMessagingToken}
				onCancel={closePermissionModal}
			/>
		</>
	);
};
